import IconCircleQuestionMark from "@src/assets/icons/lucide/circle-question-mark.svg?react";
import type { TLArrowShapeProps } from "tldraw";

// Card names
export const NOTE_CARD_SHAPE_TYPE = "card";
export const PROMPT_CARD_SHAPE_TYPE = "aicard";
export const PREPRINT_CARD_SHAPE_TYPE = "preprintCard";

// Layout binding shape types
export const CARD_SECTION_SHAPE_TYPE = "card-section";
export const CARD_SECTION_BINDING_TYPE = "card-section-binding";

// Arrow shape types
export const ARROW_SHAPE_TYPE = "arrow";
export const ARROW_BINDING_TYPE = "arrow";

export const NOTE_CARD_SHAPE_TOOL = "card";
export const AI_CARD_SHAPE_TOOL = "aicard";
export const CARD_SECTION_SHAPE_TOOL = "card-section";

export const CUSTOM_IMAGE_SHAPE_TYPE = "customImageType";

// Card sizes
// Default values
// header - 36
// body (.bn-editor) - 84
// footer - 35
// Sum - 155
export const MIN_CARD_WIDTH = 250;
export const MIN_CARD_HEIGHT = 200;
export const MAX_CARD_HEIGHT = 600;

export const DEFAULT_CARD_WIDTH = 420;
export const DEFAULT_CARD_HEIGHT = 240;

export const CARD_OFFSET = 50;
export const CARD_SPACING = 100;
export const FOLLOW_UP_PROMPT_OFFSET = 100;

// Layout binding constants
export const CARD_SECTION_PADDING = 24;
export const CARD_SECTION_HEADER_HEIGHT = 60;
export const CARD_SECTION_FOOTER_HEIGHT = 80;
export const CARD_SECTION_SPACING = CARD_SECTION_PADDING;
export const MIN_CARD_SECTION_WIDTH = MIN_CARD_WIDTH + 2 * CARD_SECTION_PADDING;
export const MIN_CARD_SECTION_HEIGHT =
	CARD_SECTION_HEADER_HEIGHT +
	MIN_CARD_HEIGHT +
	CARD_SECTION_FOOTER_HEIGHT +
	2 * CARD_SECTION_PADDING;

// Card section drag behavior: keep binding if card is within this margin (px)
export const CARD_SECTION_STICKY_THRESHOLD_PX = 150;
// Card section unbind: remove binding if card is dragged beyond this margin (px)
export const CARD_SECTION_UNBIND_THRESHOLD_PX = 300;

// Card colors
export const NOTE_CARD_PALETTE = [
	"#CF4E44",
	"#E77B6C",
	"#D4AA37",
	"#D7CB3E",
	"#90AA65",
	"#248B8E",
	"#8DB6C6",
	"#517599",
	"#A77FBC",
	"#5E5482",
	"#686868",
	"#383838",
];
export const NOTE_CARD_DEFAULT_COLOR = "#686868";

export const CLAUDE_COLOR = "#D97757";
export const GPT_COLOR = "#74AA9C";
export const DEEP_SEEK_COLOR = "#4D6BFE";
export const GROK_COLOR = "#000000";
export const GEMINI_COLOR = "#6366F1";
export const GEMMA_COLOR = "#2E96FF";
export const MOONSHOT_COLOR = "#000000";
export const KIMI_COLOR = "#020204";
export const GLM_COLOR = GROK_COLOR;
export const STABLE_DIFFUSION_COLOR = "#9D39FF";
export const NOVA_COLOR = "#FF6200";
export const NANO_BANANA_COLOR = "#FFDE5A";
export const DEFAULT_IMAGE_MODEL_COLOR = "#FFFFFF";
export const UNKNOWN_MODEL_COLOR = "#9CA3AF";
export const UNKNOWN_MODEL_ICON = IconCircleQuestionMark;

// Main menu
export const MAIN_MENU_WIDTH = 260;

// Navigation
export const ROUTES = {
	whiteboards: "/whiteboards",
	trash: "/trash",
	flowWhiteboard: "/flow-whiteboard",
	draft: "/draft",
	library: "/library",
	home: "/home",
	profile: "/profile",
	callback: "/callback",
	authEmailVerified: "/auth/email-verified",
	invite: "/invite",
	checkoutReturn: "/checkout/return",
	default: "/start-from-here",
	cliLogin: "/cli-login",
	root: "/",
} as const;

export const PARAMS = {
	shareKey: "share-key",
	shapeId: "shape-id",
	anchorId: "anchor-id",
	demoSessionId: "demo-session-id",
	demo: "demo",
} as const;

export const emailRegex =
	/^[a-zA-Z0-9.!#$%&'+/=?^_{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const PROMPT_ARROW_PROPS: TLArrowShapeProps = {
	arrowheadStart: "dot",
	size: "m",
	arrowheadEnd: "triangle",
	labelColor: "black",
	color: "black",
	fill: "solid",
	dash: "solid",
	font: "draw",
	start: { x: 0, y: 0 },
	end: { x: 0, y: 0 },
	bend: 0,
	text: "",
	labelPosition: 0,
	scale: 1,
	elbowMidPoint: 50,
	kind: "arc",
};

export const DEFAULT_ARROW_PROPS: TLArrowShapeProps = {
	arrowheadStart: "none",
	size: "m",
	arrowheadEnd: "arrow",
	labelColor: "black",
	color: "black",
	fill: "solid",
	dash: "solid",
	font: "draw",
	start: { x: 0, y: 0 },
	end: { x: 0, y: 0 },
	bend: 0,
	text: "",
	labelPosition: 0,
	scale: 1,
	elbowMidPoint: 50,
	kind: "arc",
};

// Keyboard shortcuts
export const KBD_SHORTCUTS = {
	fitToContent: "?F",
	cursorChat: "$M",
	createCardSection: "?S",
	// [[Enter]] so tldraw's shortcuts dialog renders "Enter"
	// (current tldraw kbd utils no longer map @ → Enter).
	quickAction: "$[[Enter]]",
	quickActionAllModels: "$![[Enter]]",
	cardVersionHistory: "$H",
	openBoardSearch: "$K",
	saveWhiteboard: "$S",
};

export const initialBlockId = "initialBlockId";

// Billing
export const BASE_CREDITS_PER_USD = 1000;
export const CREDIT_TOP_UP_MARKUP = 0.05; // 5% transparent markup
export const USD_PER_1000_CREDITS = 1 + CREDIT_TOP_UP_MARKUP; // $1.05 per 1,000 credits
